/* eslint-disable react/prop-types */
/**
 * DeleteDealsConfirmModal — the single confirm surface for deleting deals.
 *
 * Used by BOTH delete paths so the safeguard and the wording can't drift:
 *   - the deal detail page's header "Delete deal" button (one deal)
 *   - the deals grid's "Delete selected" toolbar button (many deals)
 *
 * Why a modal instead of the app's usual Popconfirm: deleting a deal is
 * irreversible and takes the deal's whole subtree with it. A one-click
 * Popconfirm (fine for a single child row) is too easy to fire by accident, so
 * this asks the user to TYPE a confirmation phrase and keeps Delete disabled
 * until it matches exactly.
 *
 * The phrase is the same mechanism in both paths, sized to the blast radius:
 *   - one deal   -> type that deal's key (specific: you can't delete the wrong
 *                   deal by muscle memory)
 *   - many deals -> type DELETE (typing N keys is unusable)
 *
 * App-specific by design (deal keys + the broker-deals child tables), so it
 * stays here rather than in wig-react-library.
 *
 * Props:
 *   open       : boolean
 *   dealKeys   : string[]   — natural keys being deleted; drives the title,
 *                             the listed keys, and the confirmation phrase
 *   submitting : boolean    — request in flight (spins Delete, blocks cancel)
 *   errorMsg   : string|null — server error to show; the modal stays open so
 *                             the user can read it and retry
 *   onCancel   : () => void
 *   onConfirm  : () => void — only reachable once the typed phrase matches
 */
import { useEffect, useState } from "react";
import { Alert, Input, Modal, Space, Typography } from "antd";

const { Text, Paragraph } = Typography;

// The word to type for a multi-deal delete. Single-deal deletes use the deal
// key itself (see `confirmPhrase`).
export const BULK_CONFIRM_WORD = "DELETE";

/**
 * What the user has to type to enable the Delete button.
 * @param {string[]} dealKeys
 * @returns {string}
 */
export const confirmPhrase = (dealKeys = []) =>
  dealKeys.length === 1 ? String(dealKeys[0]) : BULK_CONFIRM_WORD;

// Everything a deal delete takes with it. Spelled out rather than summarized
// as "and related records" — the user should be able to see what they lose.
const CASCADE_ITEMS = [
  "Lease/deeds, their tracts and their recordings",
  "Wells and their tracts",
  "Entities (buyers / sellers)",
  "Approvals",
  "Status history",
  "Contact log",
  "Payments",
  "Remarks",
  "Attachments",
];

const DeleteDealsConfirmModal = ({
  open,
  dealKeys = [],
  submitting = false,
  errorMsg = null,
  onCancel,
  onConfirm,
}) => {
  const [typed, setTyped] = useState("");

  // Clear the input whenever the modal (re)opens so a previous confirmation
  // can never carry over and pre-enable Delete.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const count = dealKeys.length;
  const phrase = confirmPhrase(dealKeys);
  const matches = typed.trim() === phrase;

  return (
    <Modal
      open={open}
      title={count === 1 ? "Delete deal" : `Delete ${count} deals`}
      okText="Delete"
      okButtonProps={{
        danger: true,
        disabled: !matches || submitting || count === 0,
        loading: submitting,
      }}
      cancelButtonProps={{ disabled: submitting }}
      // antd 6 prop names: `maskClosable` / `destroyOnClose` are deprecated
      // (they log a dev warning) in favour of `mask.closable` /
      // `destroyOnHidden`. Don't click away mid-request.
      mask={{ closable: !submitting }}
      onOk={onConfirm}
      onCancel={onCancel}
      destroyOnHidden
    >
      {/* antd 6 prop names again: Space `direction` -> `orientation`, Alert
          `message` -> `title`. The deprecated spellings still work but log a
          dev warning on every render. */}
      <Space orientation="vertical" size={12} style={{ width: "100%" }}>
        <Alert
          type="warning"
          showIcon
          title="This can't be undone."
          description={
            count === 1
              ? "The deal and everything filed under it is deleted permanently."
              : `All ${count} deals and everything filed under them are deleted permanently.`
          }
        />

        <div>
          <Paragraph style={{ marginBottom: 4 }}>
            Deleting {count === 1 ? "this deal" : "these deals"} also removes,
            for each one:
          </Paragraph>
          <ul style={{ margin: 0, paddingInlineStart: 20, textAlign: "left" }}>
            {CASCADE_ITEMS.map((item) => (
              <li key={item}>
                <Text type="secondary">{item}</Text>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <Text strong>
            {count === 1 ? "Deal key" : `Deal keys (${count})`}
          </Text>
          {/* Scrolls rather than stretching the modal off-screen when a large
              grid selection is being deleted. */}
          <div
            data-testid="delete-deal-keys"
            style={{
              maxHeight: 140,
              overflowY: "auto",
              marginTop: 4,
              padding: "4px 8px",
              border: "1px solid var(--ant-color-border)",
              borderRadius: 4,
              textAlign: "left",
            }}
          >
            {dealKeys.map((k) => (
              <div key={k}>
                <Text code>{k}</Text>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Text>
            Type <Text code>{phrase}</Text> to confirm.
          </Text>
          <Input
            aria-label="delete-confirmation-input"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            disabled={submitting}
            placeholder={phrase}
            style={{ marginTop: 4 }}
            // Enter submits only once the phrase matches, mirroring the
            // Delete button's own gate.
            onPressEnter={() => matches && !submitting && onConfirm?.()}
          />
        </div>

        {errorMsg && (
          <Alert
            type="error"
            showIcon
            title={count === 1 ? "Failed to delete deal" : "Delete failed"}
            description={errorMsg}
          />
        )}
      </Space>
    </Modal>
  );
};

export default DeleteDealsConfirmModal;
